/**
 * Traceur GPS d'accompagnement (bouton « ACC » sur une prestation).
 *
 * Fonctionne au niveau du module (pas d'un composant) : le suivi continue quand l'Easer change d'onglet dans
 * l'application. Les positions sont filtrées comme côté serveur (précision, doublons, téléportations), cumulées
 * en distance locale, mises en tampon dans localStorage et envoyées par paquets au serveur toutes les 8 s.
 * Au rechargement de la page, `resumeIfActive()` reprend l'accompagnement encore ouvert côté serveur.
 */
import { easerGet, easerPost } from './easerApi';

const BUFFER_KEY = 'easer_acc_buffer';
const FLUSH_MS = 8000;
const listeners = new Set();

const state = {
  session: null,       // {id, event_id, started_at, label, ...} ou null
  distanceM: 0,        // distance cumulée (serveur + points non encore envoyés)
  pointCount: 0,
  lastPos: null,       // dernier point accepté {lat, lng, ts, acc}
  gpsError: null,      // message si la géolocalisation est refusée / indisponible
  flushing: false,
};
let watchId = null;
let timer = null;
let buffer = [];
let wakeLock = null;

const emit = () => { const snap = getAccState(); listeners.forEach((cb) => { try { cb(snap); } catch { /* un écouteur cassé ne bloque pas le traceur */ } }); };

export const getAccState = () => ({ session: state.session, distanceM: state.distanceM, km: Math.round(state.distanceM / 100) / 10, pointCount: state.pointCount, gpsError: state.gpsError, pending: buffer.length });
export const subscribeAcc = (cb) => { listeners.add(cb); cb(getAccState()); return () => listeners.delete(cb); };
export const accActiveFor = (eventId) => (state.session && Number(state.session.event_id) === Number(eventId) ? state.session : null);

const haversineM = (a, b) => {
  const r = 6371000; const toRad = (d) => (d * Math.PI) / 180;
  const p1 = toRad(a.lat); const p2 = toRad(b.lat); const dp = p2 - p1; const dl = toRad(b.lng - a.lng);
  const h = Math.sin(dp / 2) ** 2 + Math.cos(p1) * Math.cos(p2) * Math.sin(dl / 2) ** 2;
  return 2 * r * Math.asin(Math.sqrt(h));
};
// Même filtre que le serveur : précision > 80 m, déplacement < 2 m ou vitesse > 30 m/s ignorés.
const accept = (prev, p) => {
  if (p.acc != null && p.acc > 80) return false;
  if (!prev) return true;
  const d = haversineM(prev, p);
  if (d < 2) return false;
  const dt = Math.max((p.ts - prev.ts) / 1000, 0.1);
  return d / dt <= 30;
};

const loadBuffer = () => { try { const raw = JSON.parse(localStorage.getItem(BUFFER_KEY) || 'null'); return raw && raw.sessionId === state.session?.id ? raw.points || [] : []; } catch { return []; } };
const saveBuffer = () => { try { if (state.session) localStorage.setItem(BUFFER_KEY, JSON.stringify({ sessionId: state.session.id, points: buffer.slice(-2000) })); else localStorage.removeItem(BUFFER_KEY); } catch { /* quota */ } };

const onPosition = (pos) => {
  if (!state.session) return;
  const p = { lat: Math.round(pos.coords.latitude * 1e6) / 1e6, lng: Math.round(pos.coords.longitude * 1e6) / 1e6, ts: pos.timestamp || Date.now(), acc: pos.coords.accuracy != null ? Math.round(pos.coords.accuracy * 10) / 10 : null };
  if (!accept(state.lastPos, p)) return;
  if (state.lastPos) state.distanceM += haversineM(state.lastPos, p);
  state.lastPos = p;
  state.pointCount += 1;
  state.gpsError = null;
  buffer.push(p);
  saveBuffer();
  emit();
};
const onGpsError = (err) => {
  state.gpsError = err?.code === 1 ? 'Géolocalisation refusée : autorisez la position pour mesurer le trajet' : 'Position GPS indisponible pour le moment';
  emit();
};

/** Envoie les points en attente ; en cas d'échec (hors ligne) ils restent dans le tampon. */
export const flushAcc = async () => {
  if (!state.session || state.flushing || buffer.length === 0) return null;
  state.flushing = true;
  const batch = buffer.splice(0, 200);
  try {
    const r = await easerPost(`/api/easer/acc/${state.session.id}/points`, { points: batch });
    // la distance serveur fait foi pour ce qui est déjà envoyé ; on y rajoute ce qui attend encore localement
    let pendingM = 0; let prev = batch[batch.length - 1];
    for (const p of buffer) { pendingM += haversineM(prev, p); prev = p; }
    state.distanceM = (r.data.distance_m || 0) + pendingM;
    saveBuffer();
    emit();
    return r.data;
  } catch (err) {
    buffer.unshift(...batch);
    if (buffer.length > 2000) buffer = buffer.slice(-2000);
    saveBuffer();
    if (err?.response?.status === 409 || err?.response?.status === 404) await stopLocal(); // terminé côté serveur (clôture auto)
    return null;
  } finally {
    state.flushing = false;
  }
};

const requestWakeLock = async () => { try { if (navigator.wakeLock && !wakeLock) wakeLock = await navigator.wakeLock.request('screen'); } catch { wakeLock = null; } };
const onVisible = () => { if (document.visibilityState === 'visible' && state.session) requestWakeLock(); };

const startWatch = () => {
  if (watchId != null || typeof navigator === 'undefined' || !navigator.geolocation) {
    if (!navigator?.geolocation) { state.gpsError = 'Géolocalisation non disponible sur cet appareil'; emit(); }
    return;
  }
  watchId = navigator.geolocation.watchPosition(onPosition, onGpsError, { enableHighAccuracy: true, maximumAge: 2000, timeout: 20000 });
  timer = setInterval(flushAcc, FLUSH_MS);
  document.addEventListener('visibilitychange', onVisible);
  requestWakeLock();
};

const stopLocal = async () => {
  if (watchId != null && navigator.geolocation) navigator.geolocation.clearWatch(watchId);
  watchId = null;
  if (timer) clearInterval(timer);
  timer = null;
  document.removeEventListener('visibilitychange', onVisible);
  if (wakeLock) { try { await wakeLock.release(); } catch { /* déjà libéré */ } wakeLock = null; }
  state.session = null; state.distanceM = 0; state.pointCount = 0; state.lastPos = null; state.gpsError = null;
  buffer = [];
  saveBuffer();
  emit();
};

const adopt = (session) => {
  state.session = session;
  state.distanceM = session.distance_m || 0;
  state.pointCount = session.point_count || 0;
  state.lastPos = session.last_position ? { ...session.last_position, ts: new Date(session.last_position.ts).getTime() } : null;
  buffer = loadBuffer();
  startWatch();
  emit();
};

/** Démarre (ou reprend) l'accompagnement sur un créneau. Résout {session, resumed, warning}. */
export const startAcc = async (eventId) => {
  const r = await easerPost(`/api/easer/events/${eventId}/acc/start`);
  adopt(r.data.session);
  return r.data;
};

/** Distance mesurée localement en km (1 décimale) pour pré-remplir la confirmation « ACC fin ». */
export const measuredKm = () => Math.round(state.distanceM / 100) / 10;

/** Termine l'accompagnement : envoie les derniers points, puis « ACC fin » côté serveur (ligne PAFK). */
export const stopAcc = async (eventId, { km, comment = '' } = {}) => {
  const session = state.session;
  await flushAcc();
  try {
    const r = await easerPost(`/api/easer/events/${eventId}/acc/stop`, { session_id: session?.id || null, km, comment });
    await stopLocal();
    return r.data;
  } catch (err) {
    // le serveur a bien clôturé le trajet mais Dolibarr n'a pas pris la ligne : on libère le traceur, l'appelant met « ACC fin » en file
    if (err?.response?.status === 424 || !err?.response) await stopLocal();
    throw err;
  }
};

/** Après un rechargement : reprend le suivi GPS d'un accompagnement encore ouvert côté serveur. */
export const resumeIfActive = async () => {
  if (state.session) return state.session;
  try {
    const r = await easerGet('/api/easer/acc/active');
    if (r.data.session) adopt(r.data.session);
    return r.data.session;
  } catch {
    return null;
  }
};

/** Oublie le traceur local (déconnexion). */
export const resetAcc = () => stopLocal();
